import React from "react";
import isFunction from "lodash/isFunction";
import PropTypes from "prop-types";
import cn from "../../../utils/cn";
import filterChildren from "../../../utils/Children/filterChildren";
import "./Table.scss";

const TableRow = ({
  id,
  className,
  clickable,
  children,
  secondaryDataList,
  style,
  hoverable,
  dataTestId,
  onCellClick,
  rowIndex,
  parent,
}) => {
  const handleCellClick = (columnIndex, event) => {
    if (parent === "table-body" && isFunction(onCellClick)) {
      onCellClick(rowIndex, columnIndex, event);
    }
  };

  const tableRowClass = cn(
    "border-b border-solid border-light-border-blue text-left app-components-table-row",
    {
      "cursor-pointer": clickable,
      "hover:bg-medium-fill-blue app-components-table-row--hoverable":
        hoverable && parent === "table-body",
    },
    className
  );

  const filteredChildren = filterChildren(children);

  const childrenWithFuncs = React.Children.map(
    filteredChildren,
    (child, index) =>
      React.cloneElement(child, {
        onCellClick: handleCellClick,
        columnIndex: index,
        secondaryDataList,
      })
  );

  const role = onCellClick ? "button" : "row";

  const handleKeyDown = (event) => {
    if (isFunction(onCellClick) && clickable && (event.key === "Enter" || event.key === " ")) {
      onCellClick(rowIndex, null, event);
    }
  };

  return (
    <tr
      id={id}
      className={tableRowClass}
      onClick={(e) => handleCellClick(null, e)}
      onKeyDown={handleKeyDown}
      style={style}
      role={role}
      tabIndex={0}
      data-testid={dataTestId}
    >
      {childrenWithFuncs}
    </tr>
  );
};

TableRow.propTypes = {
  /** id for reference */
  id: PropTypes.string,
  /** className for reference */
  className: PropTypes.string,
  /** If true, row will be clickable */
  clickable: PropTypes.bool,
  /** If true, row will be hoverable */
  hoverable: PropTypes.bool,
  /** Override the inline-styles of the root element || Not Recommended */
  style: PropTypes.object,
  /** children props for content inside */
  children: PropTypes.node,
  /** on cell click passed from the TableBody component */
  onCellClick: PropTypes.func,
  /** Row index passed from TableBody component */
  rowIndex: PropTypes.number.isRequired,
  /** Root parent component */
  parent: PropTypes.string,
  /** Data (in columns) to hide passed from TableHead or TableBody component */
  secondaryDataList: PropTypes.array,
  /** Immutable identifier for testing */
  dataTestId: PropTypes.string,
};

TableRow.defaultProps = {
  id: "",
  className: "",
  children: [],
  clickable: true,
  hoverable: true,
  onCellClick: null,
  rowIndex: 0,
  parent: "",
  secondaryDataList: [],
  style: {},
  dataTestId: "",
};

export default TableRow;
